/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText, Chip } from '@mui/material';
import { StyledDropzone } from './Dropzone';
import CancelIcon from "@mui/icons-material/Cancel";
import { ReportTagModel } from '../../Pages/ReportTag';
import { apiClient } from '../../helper/api';
import useAuth from '../../context/AuthProvider';
import Swal from 'sweetalert2';
import { GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../../helper/theme';

export interface UploadReportTagDocumentsProps {
    uploadDocumentsOpen: boolean;
    handleCloseUploadDocuments: () => void;
    reportTag: ReportTagModel;
    clientDocumentData: { reportTagId: number, files: File[]; inboxId?: number };
    actionFunction: () => void;
};
export const UploadReportTagDocuments: React.FC<UploadReportTagDocumentsProps> = ({ 
    uploadDocumentsOpen, 
    handleCloseUploadDocuments, 
    reportTag, 
    clientDocumentData, 
    actionFunction
}) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);

    const handleUploadDocuments = (files: File[]) => {
        setUploadedDocuments(files);
        const isResolvingInHouse = files.length === 0;
        const formData = new FormData();
        if (!isResolvingInHouse) {
            files.forEach((file) => {
                formData.append('files', file);
            });
        }
        formData.append('data', JSON.stringify({
            reportTagId: clientDocumentData.reportTagId,
            isResolvingInHouse: isResolvingInHouse,
            inboxId: clientDocumentData.inboxId,
        }));
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: `<div style="color: white;">${isResolvingInHouse ? 'This will resolve the tag in house' : 'This will send the documents to MISC'}</div>`,
            // text: isResolvingInHouse ? 'This will resolve the tag in house' : 'This will send the documents to MISC',
            showCancelButton: true,
            confirmButtonText: isResolvingInHouse ? 'Yes, resolve it!' : 'Yes, send it!',
            cancelButtonText: 'No, Continue to UPLOAD',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                apiClient('/ReportTags/sendTagToMisc', {
                    method: 'POST',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: formData,
                }).then((response) => {
                    if (!response) return;
                    Swal.fire({
                        title: isResolvingInHouse ? 'Tag Resolved' : 'Documents Sent to MISC',
                        text: isResolvingInHouse ? 'Tag resolved successfully' : 'Documents sent to MISC successfully',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                    setUploadedDocuments([]);
                    actionFunction();
                    handleCloseUploadDocuments();
                }).catch((error) => {
                    console.error("Error sending documents to MISC:", error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to send documents to MISC',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    };

    useEffect(() => {
        if (!uploadDocumentsOpen) {
            setUploadedDocuments([]);
        }
    }, [uploadDocumentsOpen]);

    return (
        <Dialog open={uploadDocumentsOpen} onClose={handleCloseUploadDocuments} fullWidth maxWidth="md">
            
            {loading && <LinearProgress style={{ margin: 2 }} />}
            
            <DialogTitle className="card-title">Upload Documents to resolve the issue</DialogTitle>
            <DialogContent>
                <FormHelperText className='formtexthelper-heading' sx={{ mt: 0 }}>
                    Select File(s) or Drag and Drop
                </FormHelperText>
                <StyledDropzone label="Select File(s) or Drag and Drop" 
                    onFileDrop={(files: File[]) => {
                        setUploadedDocuments([...uploadedDocuments, ...files]);
                    }} 
                />
                {uploadedDocuments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <FormHelperText className='formtexthelper-heading'>
                            Selected Files ({uploadedDocuments.length}):
                        </FormHelperText>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {uploadedDocuments.map((file, index) => (
                                <Chip key={index} label={file.name} onDelete={() => {
                                    setUploadedDocuments(uploadedDocuments.filter((_, i) => i !== index));
                                }} deleteIcon={<CancelIcon />} color="primary" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={handleCloseUploadDocuments}>Close</button>
                <button className="btn btn-primary active-btn-blue search-btn " type="button" 
                    onClick={() => handleUploadDocuments(uploadedDocuments)}
                    disabled={uploadedDocuments.length === 0}
                >Send to MISC</button>
                <button className="btn btn-primary active-btn-blue search-btn " type="button" 
                    onClick={() => handleUploadDocuments([])}
                    disabled={uploadedDocuments.length > 0}
                >Resolve in house</button>
            </DialogActions>
        </Dialog>
    );
};